import type { FC } from "react";
import { useState } from "react";

import clsx from "clsx";

import Button from "@/design-system/button";
import { FetchGet } from "@/lib/fetch-helper";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import { useQueryCustomNoSuspense } from "@/src/query-client-overrides";
import { Loading } from "@Components/Loading";
import { Panel } from "@Components/Panel";
import * as stylex from "@stylexjs/stylex";

import type { ILogoTile } from "../common/LogoTile";
import LogoTile from "../common/LogoTile";
import { type QueryXsMax, colors } from "./../../styles//tokens.stylex";

import * as css from "./BrandsPanel.module.css";

export interface IBrandsPanel {
    ajaxAction?: string;
    className?: string;
    initialData?: ILogoTile[];
}

const BrandsPanelResources = {
    title: "Marki ze zniżkami",
    showMore: "Pokaż wszystkie marki...",
};

const BrandsPanelResourcesEn = {
    title: "Brands with discounts",
    showMore: "Show more brands...",
};

const BrandsPanel: FC<IBrandsPanel> = (props) => {
    const { isMobile } = useViewportInfoContext();

    const [itemsToShow, setState] = useState(isMobile ? 6 : 10);

    const resources = languageSelector(BrandsPanelResources, BrandsPanelResourcesEn);

    const { data, isLoading } = useQueryCustomNoSuspense<ILogoTile[]>({
        queryKey: ["brands-panel", props.ajaxAction],
        queryFn: () => FetchGet<ILogoTile[]>(props.ajaxAction),
        initialData: props.initialData,
        enabled: !!props.ajaxAction,
    });

    const showMore = (): void => {
        setState(data?.length || 0);
    };

    const renderBody = (): JSX.Element => (
        <div className={clsx(css.brandsContainer, props.className)}>
            {data?.slice(0, itemsToShow).map((value, index) => (
                <div className={css.logoTileWrapper} key={index}>
                    <LogoTile {...value} />
                </div>
            ))}
        </div>
    );

    let content = null;
    if (!data || data.length === 0) {
        content = (isLoading && <Loading isModal />) || null;
    } else {
        content = renderBody();
    }

    if (data && data.length === 0) {
        return null;
    }

    return (
        <Panel title={resources.title} className={clsx(css.brandsPanel, props.className)} titleElement="h2">
            {content}
            {data && data.length > itemsToShow && (
                <Button
                    as="button"
                    size="M"
                    variant="transparent"
                    onClick={showMore}
                    styleX={[styles.btn, styles.btnColor]}
                >
                    {resources.showMore}
                </Button>
            )}
        </Panel>
    );
};

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    btnColor: {
        color: colors.fg_Accent,
    },

    btn: {
        marginBlock: "0",
        marginInline: "auto",
        display: "block",

        fontSize: { default: null, [xsMax]: "14px" },
    },
});

export default BrandsPanel;
